import { parseISO } from "date-fns";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";

import type { AdminTicketActivityDto, AdminTicketCommentDto, AdminTicketDetailsDto, UserDto } from "@/api/types";
import { TicketActivitySection } from "@/components/Ticket/TicketActivitySection";
import { TicketCommentFieldExtended } from "@/components/Ticket/TicketCommentField/TicketCommentFieldExtended";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { daysBetween, formatDistance } from "@/helpers/date";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { ActivityFeed, ActivityFeedFooter } from "@/product-patterns/tickets/ActivityFeed";
import { SortDirectionIcon } from "@/product-patterns/tickets/SortDirectionIcon";
import { ContentFieldProvider, useContentFieldManager } from "@/providers/ContentFieldProvider";
import { useTranslation } from "@/translations";

import type { TicketCommentPayload, TicketCommentType } from "../TicketCommentField/helpers";
import { TicketLogNobiSuggestionCard } from "./TicketLogNobiSuggestionCard";

interface TicketLogProps {
  ticket: AdminTicketDetailsDto;
  sessionUser: UserDto;
  activities: AdminTicketActivityDto[];
  isFetchingMoreActivities: boolean;
  hasMoreActivities: boolean;
  fetchMoreActivities: () => void;
  isActivitiesDescending: boolean;
  onToggleActivitySorting: () => void;
  onCreateComment: (payload: TicketCommentPayload) => Promise<unknown>;
  onEditComment: (
    payload: TicketCommentPayload & {
      commentId: string;
    },
  ) => Promise<void>;
}

function TicketLogInternal({
  ticket,
  sessionUser,
  activities,
  isFetchingMoreActivities,
  hasMoreActivities,
  fetchMoreActivities,
  isActivitiesDescending,
  onToggleActivitySorting,
  onCreateComment,
  onEditComment,
}: TicketLogProps): React.ReactNode {
  const [isNobiSuggestionApplied, setIsNobiSuggestionApplied] = useState(false);
  const [isNobiSuggestionDismissed, setIsNobiSuggestionDismissed] = useState(
    getLocalStorageValue(LOCAL_STORAGE_KEYS.TICKET_NOBI_SUGGESTION_DISMISSED(ticket.id), "false") !== "false",
  );
  const [editingComment, setEditingComment] = useState<AdminTicketCommentDto | undefined>(undefined);

  const postHog = usePostHog();
  const { t } = useTranslation();
  const refActivityContainer = useRef<HTMLDivElement>(null);
  const { setCurrValue } = useContentFieldManager();
  const isNobiSuggestionCardAvailable = postHog.getFeatureFlag("nobi-admin-ticket-reply-suggestion") === "card";

  useEffect(() => {
    if (!isActivitiesDescending && refActivityContainer.current) {
      refActivityContainer.current.scrollTo({ top: refActivityContainer.current.scrollHeight, behavior: "smooth" });
    }
  }, [isActivitiesDescending, activities.length]);

  const handleCreateComment = async (payload: TicketCommentPayload) => {
    await onCreateComment(payload);

    if (!refActivityContainer.current) {
      return;
    }

    if (isActivitiesDescending) {
      refActivityContainer.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      refActivityContainer.current.scrollTo({ top: refActivityContainer.current.scrollHeight, behavior: "smooth" });
    }
  };

  const handleDismissNobiSuggestion = () => {
    updateLocalStorage(LOCAL_STORAGE_KEYS.TICKET_NOBI_SUGGESTION_DISMISSED(ticket.id), "true");
    setIsNobiSuggestionDismissed(true);
  };

  const handleApplyNobiSuggestion = () => {
    setCurrValue(ticket.suggestedResponse || "");
    setIsNobiSuggestionApplied(true);
  };

  const allowedCommentTypes: TicketCommentType[] = [];
  if (ticket.canAddPublicComment) allowedCommentTypes.push("public");
  if (ticket.canAddInternalNote) allowedCommentTypes.push("internal");
  const displayedActivities: (AdminTicketActivityDto & {
    createdAt?: string;
  })[] = isActivitiesDescending
    ? [
        ...activities,
        {
          id: "activity_created",
          postedAt: ticket.createdAt,
          author: ticket.createdBy,
          createdAt: ticket.createdAt,
        },
      ]
    : [
        {
          id: "activity_created",
          postedAt: ticket.createdAt,
          author: ticket.createdBy,
          createdAt: ticket.createdAt,
        },
        ...activities,
      ];

  return (
    <SectionCard
      title={t("page.tickets.details.activities.title")}
      description={
        ticket.lastActivityBy &&
        t("page.tickets.details.activities.subtitle", {
          time: formatDistance(t, {
            start: parseISO(ticket.lastActivityAt),
          }),
          name: ticket.lastActivityBy.fullName,
        })
      }
      renderAction={() => (
        <Button
          data-testid="ticket-log-sort"
          variant="ghostPrimary"
          onClick={onToggleActivitySorting}
          icon={<SortDirectionIcon direction={isActivitiesDescending ? "descending" : "ascending"} />}
        >
          {isActivitiesDescending
            ? t("page.tickets.details.activities.sort-descending")
            : t("page.tickets.details.activities.sort-ascending")}
        </Button>
      )}
    >
      <Stack gap="md">
        <ActivityFeed
          data-testid="ticket-log-activity-feed"
          direction={isActivitiesDescending ? "normal" : "reverse"}
          ref={refActivityContainer}
        >
          {isNobiSuggestionCardAvailable &&
            !isNobiSuggestionApplied &&
            !isNobiSuggestionDismissed &&
            ticket.suggestedResponse &&
            sessionUser.isAdmin && (
              <TicketLogNobiSuggestionCard
                onApply={handleApplyNobiSuggestion}
                onDismiss={handleDismissNobiSuggestion}
                suggestion={ticket.suggestedResponse}
              />
            )}
          <TicketActivitySection
            ticket={ticket}
            daysOpen={ticket.closedAt ? daysBetween(ticket.createdAt, ticket.closedAt) : undefined}
            activities={displayedActivities}
            onNoteEdit={(comment) => setEditingComment(comment)}
            isDescending={isActivitiesDescending}
          />
          {hasMoreActivities ? (
            <ActivityFeedFooter isLoading={isFetchingMoreActivities}>
              {isFetchingMoreActivities ? (
                <Spinner size="md" />
              ) : (
                <Button data-testid="ticket-log-load-more" variant="subtle" onClick={() => fetchMoreActivities()}>
                  {t("page.tickets.details.activities.load-more")}
                </Button>
              )}
            </ActivityFeedFooter>
          ) : null}
        </ActivityFeed>
        {allowedCommentTypes.length > 0 && (
          <TicketCommentFieldExtended
            ticket={ticket}
            user={sessionUser}
            allowedCommentTypes={allowedCommentTypes}
            allowedAIActions={["replyGeneration", "responsibilitySuggestion", "replySuggestion"]}
            onComment={handleCreateComment}
            onEditComment={onEditComment}
            onCancelEditComment={() => setEditingComment(undefined)}
            {...{
              editingComment,
            }}
          />
        )}
      </Stack>
    </SectionCard>
  );
}

// `ContentFieldProvider` is needed for other components to interact with
// the nested comment field input (via `useContentFieldManager`)
export function TicketLog(props: TicketLogProps): React.ReactNode {
  return (
    <ContentFieldProvider>
      <TicketLogInternal {...props} />
    </ContentFieldProvider>
  );
}
